import {useInfiniteQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords} from '../services/MeterAssignmentsService';
import {useState} from 'react';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {MeterAssignment} from '../types';

export const useMeterAssignments = () => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<MeterAssignment[]>([]);

    const sort = {column: 'created', direction: 'desc' as const};

    // INFINITE QUERY
    const meterAssignmentsQuery = useInfiniteQuery({
        queryKey: ['meterAssignments', keyword, limit, sort.column, sort.direction],
        queryFn: ({pageParam}: { pageParam: string | null }) => fetchRecords({limit, sort, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    // DELETE MUTATION — FNP-217: delete by composite key (customerId, sortKey),
    // so we delete whole records, not bare ids.
    const deleteMutation = useMutation({
        mutationFn: async (records: Pick<MeterAssignment, 'customerId' | 'sortKey'>[]) => {
            const promises = records.map(record => deleteRecord(record));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Meter Assignment deleted');
            queryClient.invalidateQueries({queryKey: ['meterAssignments']});
        },
        onError: notifyError
    });

    const fetchRecordsAction = () => {
        if (meterAssignmentsQuery.hasNextPage) {
            meterAssignmentsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (records?: Pick<MeterAssignment, 'customerId' | 'sortKey'>[]) => {
        const selectedRecords = records || selection;
        if (selectedRecords.length > 0) {
            deleteMutation.mutate(selectedRecords);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = meterAssignmentsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: meterAssignmentsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: meterAssignmentsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};
